import Section from '../../components-library/checkout/sections/Section';
import useBasket from '../../hooks/basket/useBasket';
import classnames from 'classnames';
import OrderBasketItem from '../../components-library/basket/order-basket-item';
import useScreenType from '../../hooks/utility/useScreenType';
import Tips from '../../components-library/checkout/Tips';
import useRouter from '../../hooks/navigation/useRouter';
import BackIcon from '../../assets/logo/BackIcon';
import useTheme from '../../hooks/ui/useTheme';
import CheckoutInfoBag, { CheckoutInfoBagHandle } from '../../components-library/checkout/sections/CheckoutInfoBag';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import useDraftError from '../../hooks/order/useDraftError';
import EmptyBasketIllustration from '../../assets/illustrations/EmptyBasketIllustration';
import LoadingPage from '../../components-library/loading/LoadingPage';
import OrderDraftErrorPage from '../../components-library/checkout/OrderDraftErrorPage';
import { useTranslation } from 'react-i18next';
import OrderPaymentBreakdown from '../../components-library/order/OrderPaymentBreakdown';
import DiscountDisplacementNotice from '../../features/discount-exclusivity/DiscountDisplacementNotice';
import { CompleteBasketItem } from '../../providers/BasketProvider';
import DeliveryThreshold from '../../components-library/delivery/DeliveryThreshold';
import { SCREENS } from '../../services/exports/Constants';
import CheckoutThreshold from '../../components-library/checkout/CheckoutThreshold';
import React, { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import FixedPageLayout from '../../components-library/layouts/FixedPageLayout';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import useInitialData from '../../hooks/global/useInitialData';
import ProceedToPaymentButton from '../../components-library/checkout/ProceedToPaymentButton';
import CheckoutButton from '../../components-library/buttons/CheckoutButton';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import ExpectedCashback from '../../components-library/cashback/ExpectedCashback';
import TrustLabel from '../../components-library/labels/TrustLabel';
import BasketItemModifiers from '../../components-library/basket/BasketItemModifiers';
import OfferLineBreakdown from '../../features/offers/components/OfferLineBreakdown';
import OrderLineBadges from '../../components-library/order/OrderLineBadges';
import useWarmUpStripeElements from '../../hooks/payment/useWarmUpStripeElements';
import TestRestaurantNotice from '../../components-library/company/TestRestaurantNotice';
import useTableOrder from '../../hooks/order/useTableOrder';
import { useLeaveCheckoutWhenTableVisitEnds } from '../../hooks/order/useTableVisitGuard';

interface CheckoutScreenProps {
  onBasketItemUpdateClick?: (item: CompleteBasketItem) => void;
  className?: string;
}

interface ICheckoutScreen {
  items: CompleteBasketItem[];
  checkoutInfoBagRef: React.RefObject<CheckoutInfoBagHandle>;
  onBasketItemUpdate: (item: CompleteBasketItem) => void;
  hasTips: boolean;
  company?: { is_test_restaurant?: boolean } | null;
  className?: string;
}

function OrderConfirmationScreen(props: CheckoutScreenProps) {
  const { onBasketItemUpdateClick, className } = props;

  const { items } = useBasket();
  const { data } = useOrderFlow();
  const { data: initialData } = useInitialData();
  const { company } = initialData;
  const { isDesktop } = useScreenType();
  const { isMobileApp } = useMobileApp();
  const { toggleMenuItemModal, toggleOrderMethodModal } = useAppMethods();
  const { checkoutInfoBagRef } = usePaymentFlow();
  const { isOrderMethodInfoComplete, setIgnorePaymentMethod } = useOrderFlow();

  // The payment screen comes next: its Stripe Elements then draw at once, the save box included.
  useWarmUpStripeElements();

  const hasTips = company?.has_tips;

  const onBasketItemUpdate = (item: CompleteBasketItem): void => {
    if (onBasketItemUpdateClick) {
      return onBasketItemUpdateClick(item);
    }

    toggleMenuItemModal({
      openModal: true,
      product: item.entry,
      lineItem: item,
      basketItemIndex: item.original_index,
    });
  };

  // The order-method modal below is this screen's own recovery for anything about the
  // delivery details — a missing address, an address the backend rejected — so those
  // failures keep the loader + modal rather than the error page. `recoversDeliveryDetails`
  // says so; the hook still returns show=true for them in the native app, where the effect
  // below is skipped and there would otherwise be no recovery at all.
  const { isTableOrder } = useTableOrder();
  useLeaveCheckoutWhenTableVisitEnds();
  const { show: showDraftError, isDeliveryDetails } = useDraftError({
    recoversDeliveryDetails: true,
    recoversCustomerInput: true,
  });

  useEffect(() => {
    // A failed delivery draft also clears isOrderMethodInfoComplete; without the
    // showDraftError guard this would open the order-method modal underneath the
    // full-screen OrderDraftErrorPage.
    // A table order has nothing to fix in that modal: the table comes from its QR code. One whose
    // visit ran out is dropped by SideEffectsProvider, which sends the guest back to the menu
    // (useLeaveCheckoutWhenTableVisitEnds); the backend has no expiry on a table.
    if (!data || showDraftError || isOrderMethodInfoComplete || isMobileApp || isTableOrder) {
      return;
    }

    const timeout = setTimeout(() => toggleOrderMethodModal({ openModal: true, showReturn: true }), 1000);

    return () => clearTimeout(timeout);
  }, [data, showDraftError, isOrderMethodInfoComplete, isTableOrder]);

  useEffect(() => {
    setIgnorePaymentMethod(true);

    return () => setIgnorePaymentMethod(false);
  }, []);

  const config = {
    items,
    checkoutInfoBagRef,
    onBasketItemUpdate,
    hasTips,
    company,
    className,
  };

  if (showDraftError) {
    // This screen IS SCREENS.CHECKOUT, so the way out has to be the basket, not here.
    return <OrderDraftErrorPage backTo="basket" isDeliveryDetails={isDeliveryDetails} />;
  }

  return (
    <div className="min-h-screen bg-gray-background">
      {!data.id && <LoadingPage />}
      {isDesktop ? <Desktop {...config} /> : <Mobile {...config} />}
    </div>
  );
}

export default OrderConfirmationScreen;

const Desktop = ({ checkoutInfoBagRef, hasTips, onBasketItemUpdate, company }: ICheckoutScreen) => {
  const { items } = useBasket();
  const { data, loading } = useOrderFlow();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderConfirmationScreen' });

  return (
    <FixedPageLayout
      hasHeader={false}
      rightColumnGrow
      topContent={
        company?.is_test_restaurant && <TestRestaurantNotice company={company} className="layout-container mt-md" />
      }
      leftColumn={
        <OrderConfirmationScreen.LeftColumn>
          {Boolean(data.id) && <CheckoutInfoBag ref={checkoutInfoBagRef} />}
        </OrderConfirmationScreen.LeftColumn>
      }
      rightColumn={
        <>
          {items.length > 0 ? (
            // One scroll container for the whole summary (items + totals + note + continue), so
            // nothing is clipped and it scrolls as one panel — matches the prototype checkout.
            <div className="flex-1 min-h-0 overflow-y-auto p-md space-y-md scrollbar-hide">
              <h2 className="text-base font-semibold text-brand-text-primary">{t('sections.order_summary')}</h2>
              <DeliveryThreshold />
              <div className="space-y-md">
                {items.map((item: CompleteBasketItem, index) => (
                  <OrderBasketItem
                    key={`basket-item-${index}`}
                    coupon_type={data.coupon_type}
                    data={item}
                    index={index}
                    editable
                    onUpdateClick={() => onBasketItemUpdate({ ...item, original_index: index })}
                    classNames={{
                      itemContainer: '!border-none !p-none',
                      counterContainer: '!rounded-full !px-xs',
                    }}
                  />
                ))}
              </div>
              {/* Info label sits below the basket items, above the totals (prototype). */}
              <DiscountDisplacementNotice />
              <hr />
              <OrderPaymentBreakdown
                order={data}
                loading={loading}
                withCashbackPointsRow
                withPromoOrGiftCardRow
                variant="overview"
                tipsRender={() => hasTips && <Tips />}
              />
              <CheckoutThreshold />
              <ExpectedCashback />
              <TrustLabel />
              <ProceedToPaymentButton />
            </div>
          ) : (
            <div className="flex items-center justify-center p-md">
              <EmptyBasket />
            </div>
          )}
        </>
      }
    />
  );
};

type LeftColumnProps = PropsWithChildren<{ onReturn?: () => void }>;

OrderConfirmationScreen.LeftColumn = ({ children, onReturn }: LeftColumnProps) => {
  const { navigate } = useRouter();
  const theme = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderConfirmationScreen' });
  const _onReturn = onReturn ? onReturn : () => navigate({ web: { to: SCREENS.HOME_SCREEN } });

  return (
    <div className="pb-lg">
      <button
        onClick={_onReturn}
        className="flex items-center w-full p-md text-sm text-muted-foreground lg:p-none lg:w-fit"
      >
        <BackIcon className="h-3 !w-3 mr-xs" color={theme.color.gray['700']} />
        {t('buttons.return')}
      </button>
      {children}
    </div>
  );
};

const Mobile = ({ checkoutInfoBagRef, hasTips, className, company }: ICheckoutScreen) => {
  const { items } = useBasket();
  const { data, loading } = useOrderFlow();
  const { navigate } = useRouter();
  const { isMobileApp } = useMobileApp();
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderConfirmationScreen' });
  const { formatCurrency } = useAppMethods();

  const [itemsExpanded, setItemsExpanded] = useState(false);

  const itemCount = items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <FixedPageLayout
      // Inside the layout: on mobile web it is a fixed full-viewport container, so a
      // sibling rendered before it would be painted underneath and never seen.
      topContent={company?.is_test_restaurant && <TestRestaurantNotice company={company} className="mx-md mt-md" />}
      footer={
        items.length > 0 &&
        !isMobileApp && (
          <CheckoutButton className={classnames('bg-brand-card')}>
            <ProceedToPaymentButton />
          </CheckoutButton>
        )
      }
    >
      <div className={classnames('grid grid-cols-12', className)}>
        <div className="col-span-12 lg:col-span-8 xl:col-span-9">
          {!isMobileApp && (
            <button type="button" onClick={() => navigate({ web: { to: SCREENS.BASKET } })} className={returnLinkStyle}>
              <ChevronLeft className="w-4 h-4" />
              {t('buttons.return')}
            </button>
          )}
          {Boolean(data.id) && <CheckoutInfoBag ref={checkoutInfoBagRef} />}
        </div>
        <div className="col-span-12 lg:col-span-4 xl:col-span-3">
          {items.length > 0 ? (
            <Section show title="" className="!px-md py-lg">
              <div className="rounded-xl border-xs border-brand-border bg-brand-card p-md">
                <button
                  type="button"
                  onClick={() => setItemsExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-xs">
                    <h3 className="text-base font-semibold text-brand-text-primary">{t('sections.order_summary')}</h3>
                    <span className="text-xs text-muted-foreground">
                      ({t('labels.item_count', { count: itemCount })})
                    </span>
                  </div>
                  <div className="flex items-center gap-xs">
                    <span className="text-sm font-semibold text-brand-text-primary tabular-nums">
                      {/* The SUBTOTAL, not a total. This block is the one place on the overview that
                          still carries a figure; the summary below deliberately shows no price at all. */}
                      {formatCurrency(data.sub_total)}
                    </span>
                    <ChevronDown
                      className={classnames('w-4 h-4 text-muted-foreground transition-transform duration-200', {
                        'rotate-180': itemsExpanded,
                      })}
                    />
                  </div>
                </button>
                {itemsExpanded && (
                  <div className="mt-md space-y-sm border-t-xs border-brand-border pt-md">
                    <DeliveryThreshold />
                    {items.map((item, index) => (
                      <div key={`basket-item-${index}`} className="space-y-2xs">
                        <div className="text-sm flex justify-between gap-md">
                          <span className="text-brand-text-primary">
                            {item.quantity}x {item.entry?.name}
                          </span>
                          <span className="text-brand-text-primary shrink-0">{formatCurrency(item.actual_price)}</span>
                        </div>
                        {/* Free-item "1 × free" + Gratisartikel badge — was web/payment only; add on
                            mobile order summary too (founder 2026-08-06). */}
                        <OfferLineBreakdown item={item} />
                        <OrderLineBadges badges={item.badges} />
                        <BasketItemModifiers item={item} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-md pt-md border-t-xs border-brand-border space-y-md">
                  <DiscountDisplacementNotice />
                  <OrderPaymentBreakdown
                    order={data}
                    loading={loading}
                    withCashbackPointsRow
                    withPromoOrGiftCardRow
                    variant="overview"
                    tipsRender={() => hasTips && <Tips />}
                  />
                  <ExpectedCashback />
                  <TrustLabel />
                  <CheckoutThreshold />
                </div>
              </div>
            </Section>
          ) : (
            <EmptyBasket />
          )}
        </div>
      </div>
    </FixedPageLayout>
  );
};

const returnLinkStyle =
  'flex items-center gap-2xs text-sm text-brand-text-primary hover:text-brand-primary transition-colors pt-md px-md lg:px-none lg:pt-lg lg:pb-md';

const EmptyBasket = (): ReactElement => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Pages:Checkout:OrderConfirmationScreen' });

  return (
    <div className="grow w-full flex flex-col justify-center items-center z-1">
      <EmptyBasketIllustration />
      <p className="text-center text-sm text-muted-foreground">{t('labels.add_items')}</p>
    </div>
  );
};
